/** COMMON IMPORTS */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Switch, Route, useHistory, useLocation } from "react-router-dom";
import { UserContext } from "./components/reuseableComponents/auth/UserContext";
import { EnvironmentProvider } from "./configuration/EnvironmentContext";
import URL404 from "./components/reuseableComponents/URL404";
import Footer from "./components/reuseableComponents/navigation/Footer";

/** ROUTE IMPORTS */
import { ddwAdminRoutes } from "./routes/ddwAdminRoutes.js";
import { enforcementRoutes } from "./routes/enforcementRoutes";
import { engineeringRoutes } from "./routes/engineeringRoutes.js";
import { publicRoutes } from "./routes/publicRoutes";
import { portalRoutes } from "./routes/portalRoutes";

/** UTAH DESIGN SYSTEM IMPORTS */
import { appConfig } from "./configuration/AppConfig";
import { setUtahHeaderSettings } from "@utahdts/utah-design-system-header";
import logo from "./images/DDW_LOGOS/Logo-DDW.svg"
import "@utahdts/utah-design-system/css";
import "@utahdts/utah-design-system-header/css";
import "./css/utahDesign.css";

/** Dynamic Data for Menu Items */
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { GET_MENU_ITEMS } from "./queries/menuQrys";
import { GET_WATER_SYSTEM_SUMMARY } from "./queries/publicQrys/SystemHeaderViewQrys";
import { GET_LOGGEDIN_USER_QUERY } from "./queries/ddwAdminQrys/UserManagementQrys";

/** Sidebar Imports */
import SidePanel from "./components/reuseableComponents/SidePanel.js";
import WaterSystemSearchModal from "./components/reuseableComponents/WaterSystemSearchModal";
import { WaterSystemSearchModalProvider, useWaterSystemSearchModal } from "./components/reuseableComponents/WaterSystemSearchModalContext.js";
import WaterSystemGuardRoute from "./components/reuseableComponents/WaterSystemGuardRoute";

/** Status Bar Imports */
import StatusBar from "./components/reuseableComponents/StatusBar";
import { HoverProvider } from "./context/HoverContext.js";
import { getPwsIdCookie } from "./utilities/commonFunctions/pwsIdCookie";
import { setUserProfile } from "./components/reuseableComponents/auth/UserProfiles.js";

/**
 * Utility to retrieve a cookie value by name
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const ACCESS_TOKEN_KEY = "token";

/**TODO:
 * remove Header.js once we finish refacturing code.
 */
/**
 * Announces page navigations to screen readers via a visually-hidden aria-live region.
 * Must be rendered inside <Router> so useLocation is available.
 */
const RouteAnnouncer = () => {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const h1 = document.querySelector('h1');
      setAnnouncement(h1 ? `Navigated to ${h1.textContent}` : 'Page changed');
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="visually-hidden"
    >
      {announcement}
    </span>
  );
};

function HeaderComponent({ setPanelMenu, setIsSidePanelVisible, isSidePanelVisible, providerUser, logout, menuItemsArray, setAuthCookie, redirectPath, setRedirectPath, panelMenu }) {
  const history = useHistory();
  const lastPathRef = useRef(null);

  useEffect(() => {
    if (redirectPath) {
      history.push(redirectPath);
      setRedirectPath(null);
    }
  }, [redirectPath, history, setRedirectPath]);

  // Add this effect to set sidePanelMenu based on URL when route changes
  // Listens to history changes and auto-opens the panel for new route sections
  useEffect(() => {
    if (!menuItemsArray || menuItemsArray.length === 0) return;

    const handleLocationChange = () => {
      const pathname = window.location.pathname;

      // Don't auto-open on root pages
      if (pathname === "/" || pathname === "/public-index") {
        lastPathRef.current = null;
        return;
      }

      const pathMatch = [
        { key: "home", code: "HOME" },
        { key: "ddw-admin", code: "DDW_ADMIN" },
        { key: "portal", code: "PORTAL" },
        { key: "public", code: "PUBLIC" }
      ].find(m => pathname.includes(m.key));

      if (pathMatch && lastPathRef.current !== pathMatch.code) {
        setIsSidePanelVisible(true);
        setPanelMenu(pathMatch.code);
        lastPathRef.current = pathMatch.code;
      }
    };

    // Listen to history changes
    const unlisten = history.listen(handleLocationChange);
    
    // Also run on initial mount
    handleLocationChange();

    return unlisten;
  }, [menuItemsArray, setPanelMenu, setIsSidePanelVisible, history]);

  useEffect(() => {
    setUtahHeaderSettings({
      title: "Waterlink",
      utahId: {
        currentUser: providerUser?.user?.firstName ? {
          first: providerUser.user.firstName,
          last: providerUser.user.lastName,
          email: providerUser.user.email,
          authenticated: sessionStorage?.auth ? JSON.parse(sessionStorage.auth) : false
        } : null,
        onSignIn: () => {
          setAuthCookie("v2Authenticated", "true");
          window.location.assign(`${appConfig.authEndpoint}${appConfig.authParams}`);
        },
        onSignOut: () => {
          logout();
        },
        onProfile: () => {
          window.open('https://id.utah.gov', '_blank', 'noopener,noreferrer');
        },
      },
      logo: {
        htmlString: '<img src="' + logo + '" id="ddw-logo" alt="Logo-DDW" />'
      },
      mainMenu: {
        menuItems: Array.isArray(menuItemsArray) ? menuItemsArray : [],
      },
      footer: {
        domLocationTarget: {
          cssSelector: "#app-footer",
        },
        showHorizontalRule: true,
      },
    });
  }, [providerUser, logout, menuItemsArray, setAuthCookie]);

  return null;
}


const CookieWaterSystemRestorer = ({ setSelectedWaterSystem, user }) => {
  const { setIsRestoringFromCookie, setSelectedWaterSystem: setContextWaterSystem } = useWaterSystemSearchModal();
  const [getWaterSystemFromCookie, { data: cookieSystemData, error: cookieError }] = useLazyQuery(GET_WATER_SYSTEM_SUMMARY);
  const client = useApolloClient();
  const prevUserIdRef = useRef(user?.umdId ?? null);

  const applyWaterSystem = useCallback((waterSystem) => {
    sessionStorage.setItem("selectedWaterSystem", JSON.stringify(waterSystem));
    setSelectedWaterSystem(waterSystem);
    setContextWaterSystem(waterSystem);
  }, [setSelectedWaterSystem, setContextWaterSystem]);

  useEffect(() => {
    if (!sessionStorage.getItem("selectedWaterSystem")) {
      const pwsId = getPwsIdCookie();
      if (pwsId) {
        getWaterSystemFromCookie({ variables: { pwsId } });
      } else {
        setIsRestoringFromCookie(false);
      }
    }
  }, [getWaterSystemFromCookie, setIsRestoringFromCookie]);

  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    const currentUserId = user?.umdId ?? null;
    prevUserIdRef.current = currentUserId;

    if (!prevUserId && currentUserId) {
      const pwsId = getPwsIdCookie();
      if (pwsId) {
        client.query({
          query: GET_WATER_SYSTEM_SUMMARY,
          variables: { pwsId },
          fetchPolicy: "network-only",
        }).then(({ data }) => {
          if (data?.getWaterSystemSummary) {
            applyWaterSystem(data.getWaterSystemSummary);
          }
        }).catch((error) => {
          console.warn("Failed to restore water system from cookie on login:", error);
        });
      }
    }
  }, [user, client, applyWaterSystem]);

  useEffect(() => {
    if (cookieSystemData?.getWaterSystemSummary) {
      applyWaterSystem(cookieSystemData.getWaterSystemSummary);
      setIsRestoringFromCookie(false);
    }
  }, [cookieSystemData, applyWaterSystem, setIsRestoringFromCookie]);

  useEffect(() => {
    if (cookieError) {
      setIsRestoringFromCookie(false);
    }
  }, [cookieError, setIsRestoringFromCookie]);

  return null;
};

const App = () => {
  const client = useApolloClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [user, setUser] = useState(
    sessionStorage.getItem("user") ? JSON.parse(sessionStorage.user) : (
      sessionStorage.defaultUser ? JSON.parse(sessionStorage.defaultUser) : null
    )
  );

  const [menuItemsArray, setMenuItemsArray] = useState([]);
  const [rawMenuItemsArray, setRawMenuItemsArray] = useState([]);
  const [isSidePanelVisible, setIsSidePanelVisible] = useState(false);
  const [panelMenu, setPanelMenu] = useState("");
  const [selectedWaterSystem, setSelectedWaterSystem] = useState(
    sessionStorage.getItem("selectedWaterSystem")
      ? JSON.parse(sessionStorage.getItem("selectedWaterSystem"))
      : null
  );
  const [redirectPath, setRedirectPath] = useState(null);
  const hasInitialized = useRef(false);
  const lastVisibleMenuRef = useRef(""); // Track the last visible menu for smooth transitions

  const destroyAuthCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  // Track the last visible menu so content persists during collapse animation
  useEffect(() => {
    if (panelMenu !== "") {
      lastVisibleMenuRef.current = panelMenu;
    }
  }, [panelMenu]);

  const getChildMenuItems = useCallback((children) => {
    return children.map((child) => ({
      title: child.label.toUpperCase(),
      isSelected: false,
      ...(child.children?.length > 0
        ? { actionMenu: getChildMenuItems(child.children), childrenMenuType: 'DROPDOWN' }
        : { actionUrl: { url: child.url_route } }
      )
    }));
  }, []);

  const processMenu = useCallback((menuData) => {
    if (!menuData || !menuData.getMenuItems) return [];

    return menuData.getMenuItems.sections.map((menuItem) => {
      if (menuItem.children && menuItem.children.length > 0) {
        return {
          id: menuItem.code,
          title: menuItem.label.toUpperCase(),
          isSelected: (panelMenu === menuItem.code),
          actionFunction: () => {
            // Use the current panelMenu state, not the captured value
            // Toggle logic: if same menu is clicked, close it; otherwise open it
            setPanelMenu((prevPanelMenu) => {
              if (prevPanelMenu === menuItem.code) {
                // Toggle off: collapse the panel
                setIsSidePanelVisible(false);
                return "";
              } else {
                // Toggle on: expand the panel with the new menu
                setIsSidePanelVisible(true);
                return menuItem.code;
              }
            });
          },
          children: menuItem.children,
          formattedChildren: getChildMenuItems(menuItem.children)
        };
      } else {
        return {
          title: menuItem.label.toUpperCase(),
          isSelected: (panelMenu === menuItem.code),
          actionUrl: {
            url: menuItem.url_route,
          },
        };
      }
    });
  }, [panelMenu, getChildMenuItems, setIsSidePanelVisible]);

  /** * Orchestrates the sequential loading of User and Menu
   */
  const initializeApp = useCallback(async () => {
    // if (isSyncing) return;
    console.log("Initializing application...");

    try {
      setIsSyncing(true);
      const v2AuthCookie = getCookie("v2Authenticated");

      let currentUser = user;

      const userFromStorage = sessionStorage.getItem("user");
      const savedSystem = sessionStorage.getItem("selectedWaterSystem");
      if (savedSystem) {
        setSelectedWaterSystem(JSON.parse(savedSystem));
      }

      // Fetch user data if we don't already have it, but have successfully logged in to either environment
      if (!userFromStorage && (v2AuthCookie === "true")) {
        console.log("User not in storage but authenticated. Fetching user data...");

        try {
          const { data: userData } = await client.query({
            query: GET_LOGGEDIN_USER_QUERY,
            fetchPolicy: 'network-only',
          });

          if (userData?.getLoggedInUser) {
            console.log("User fetched successfully:", userData.getLoggedInUser.umdId);
            const validatedUser = setUserProfile(userData.getLoggedInUser);

            // Update both React state AND local reference
            setUser(validatedUser);
            currentUser = validatedUser;

            // Persist to sessionStorage for future loads
            sessionStorage.setItem("user", JSON.stringify(validatedUser));
            sessionStorage.setItem("auth", "true");

            if (validatedUser.permissions.length === 0) {
              console.warn("User has no permissions. Redirecting to access request.");
              setRedirectPath("/public/request-access");
            }
          } else {
            console.warn("GET_LOGGEDIN_USER_QUERY returned no user data");
          }
        } catch (userFetchErr) {
          console.error("Failed to fetch user data:", userFetchErr);
          throw userFetchErr; // Don't continue if user fetch fails
        }
      } else if (userFromStorage) {
        console.log("User already in storage. Using cached data.");
        currentUser = JSON.parse(userFromStorage);
      } else {
        console.log("Not authenticated (v2AuthCookie missing). User will be null.");
      }

      console.log("Current user after initialization:", currentUser);

      try {
        const { data: menuData } = await client.query({
          query: GET_MENU_ITEMS,
          variables: { pwsId: selectedWaterSystem?.pwsId || null },
          fetchPolicy: 'network-only',
        });

        if (menuData?.getMenuItems) {
          console.log("Menu items fetched successfully");
          setRawMenuItemsArray(() => menuData.getMenuItems.sections || []);
          setMenuItemsArray(() => processMenu(menuData));
        } else {
          console.warn("GET_MENU_ITEMS returned no menu data");
          setRawMenuItemsArray([]);
          setMenuItemsArray([]);
        }
      } catch (menuFetchErr) {
        console.error("Failed to fetch menu items:", menuFetchErr);
        setRawMenuItemsArray([]);
        setMenuItemsArray([]);
        throw menuFetchErr;
      }

    } catch (err) {
      console.error("App initialization failed:", err);
    } finally {
      setIsSyncing(false); // Unlocks the UI
    }
  }, [client, processMenu, selectedWaterSystem, user]);

  useEffect(() => {
    if (!isSyncing && !hasInitialized.current) {
      initializeApp();
      hasInitialized.current = true;
    }
  }, [isSyncing, initializeApp]);

  // Refetch menu when selectedWaterSystem changes (after initial load)
  useEffect(() => {
    if (hasInitialized.current && selectedWaterSystem?.pwsId) {
      const refetchMenu = async () => {
        try {
          const { data: menuData } = await client.query({
            query: GET_MENU_ITEMS,
            variables: {
              pwsId: selectedWaterSystem.pwsId
            },
            fetchPolicy: 'network-only',
          });
          setRawMenuItemsArray(() => menuData.getMenuItems.sections || []);
          setMenuItemsArray(() => processMenu(menuData));
        } catch (err) {
          console.error("Menu refetch failed:", err);
        }
      };
      refetchMenu();
    }
  }, [selectedWaterSystem?.pwsId, client, processMenu]);

  const providerUser = useMemo(() => ({
    user,
    setUser,
    isSyncing // Pass this down so PrivateRoute can see it
  }), [user, setUser, isSyncing]);

  // Add this inside the App component
const processedMenuItems = useMemo(() => {
  return menuItemsArray.map(item => ({
    ...item,
    isSelected: item.id === panelMenu
  }));
}, [menuItemsArray, panelMenu]);

  const setAuthCookie = useCallback((name, value) => {
    const expires = new Date(Date.now() + 10 * 60 * 60 * 1000).toUTCString(); // 10 hours
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }, []);

  const logout = () => {
    destroyAuthCookie("v2Authenticated");
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("auth");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("selectedWaterSystem");

    const revokeUrl = token && token !== "null"
        ? `${appConfig.revoke}${appConfig.authParams}&jwt=${token}`
        : `${appConfig.revoke}${appConfig.authParams}`;

    window.location.assign(revokeUrl);
  };

  return (
    <EnvironmentProvider>
      <UserContext.Provider value={providerUser}>
        <WaterSystemSearchModalProvider menuItemsArray={rawMenuItemsArray}>
          <HoverProvider>
            <Router>
              <nav aria-label="Skip links">
                <a href="#main-content" className="skip-to-main-content">Skip to main content</a>
              </nav>
              <CookieWaterSystemRestorer setSelectedWaterSystem={setSelectedWaterSystem} user={user} />
              <HeaderComponent
                setPanelMenu={setPanelMenu}
                setIsSidePanelVisible={setIsSidePanelVisible}
                isSidePanelVisible={isSidePanelVisible}
                setMenuItemsArray={setMenuItemsArray}
                providerUser={providerUser}
                logout={logout}
                user={user}
                menuItemsArray={processedMenuItems}
                panelMenu={panelMenu}
                setAuthCookie={setAuthCookie}
                redirectPath={redirectPath}
                setRedirectPath={setRedirectPath}
              />
              <WaterSystemSearchModal
                selectedWaterSystem={selectedWaterSystem}
                setSelectedWaterSystem={setSelectedWaterSystem}
              />
              <div className="waterlink-container">
                <nav className={`side-panel-container ${isSidePanelVisible ? 'visible' : 'hidden'}`} aria-label="Main navigation">
                  {Array.isArray(processedMenuItems) && (() => {
                    // Use panelMenu if panel is visible, otherwise use lastVisibleMenuRef to keep content during collapse
                    const menuToDisplay = isSidePanelVisible ? panelMenu : lastVisibleMenuRef.current;
                    const activeMenuItem = processedMenuItems.find(item => item.id === menuToDisplay);
                    return activeMenuItem && activeMenuItem.children ? (
                      <SidePanel
                        menuItems={activeMenuItem.children}
                        selectedWaterSystem={selectedWaterSystem}
                      />
                    ) : null;
                  })()}
                </nav>
                <main id="main-content" className="main-content-container d-flex flex-column">
                  <RouteAnnouncer />
                  {isSyncing ? (
                    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
                    <div className="utah-id-syncing" role="status" aria-live="polite"><em>Checking Login Status...</em></div>
                  ) : (
                    <Switch>
                      {/* PUBLIC ROUTES */}
                      {publicRoutes.map((entry) =>
                        entry.requiresWaterSystem ? (
                          <WaterSystemGuardRoute
                            key={entry.path}
                            exact
                            path={entry.path}
                            component={entry.component}
                          />
                        ) : (
                          <Route
                            key={entry.path}
                            exact
                            path={entry.path}
                          >
                            <entry.component
                              selectedWaterSystem={selectedWaterSystem}
                              setSelectedWaterSystem={setSelectedWaterSystem}
                            />
                          </Route>
                        )
                      )}
                      {/*SECURE ROUTES*/}
                      {ddwAdminRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      {enforcementRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      {engineeringRoutes.map((entry) =>
                      entry.requiresWaterSystem ? (
                        <WaterSystemGuardRoute
                          key={entry.path}
                          exact={entry.exact !== false}
                          path={entry.path}
                          component={entry.component}
                        />
                      ) : (
                        <Route
                          key={entry.path}
                          exact={entry.exact !== false}
                          path={entry.path}
                        >
                          <entry.component
                            selectedWaterSystem={selectedWaterSystem}
                            setSelectedWaterSystem={setSelectedWaterSystem}
                          />
                        </Route>
                      ))}
                      {portalRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      <Route component={URL404} />
                    </Switch>
                  )}
                  <StatusBar />
                </main>
              </div>
              <Footer />
            </Router>
          </HoverProvider>
        </WaterSystemSearchModalProvider>
      </UserContext.Provider>
    </EnvironmentProvider>
  );
}

export default App;
